import React, { useCallback } from 'react';
import memoizeOne from 'memoize-one';
import { PARENT_HIERARCHY_TYPE } from '@atlassian/jira-issue-type-hierarchies';
import {
	ChangeEventTypes,
	type ChangeEvent,
} from '@atlassian/jira-issue-view-model/src/change-type';
import type { ExternalAction } from '@atlassian/jira-issue-view-store/src/actions/external-actions';
import { FixVersionsKey, StatusKey, TimeEstimateKey } from '@atlassian/jira-providers-issue';
import { connect } from '@atlassian/jira-react-redux';
import { getExtractedFields } from '../../common/hooks/use-store-fields';
import type { PanelType } from '../../model/panels/types';
import type { CardId, CardKey } from '../../model/types';
import { useUpdateEpicSummary } from '../../services/mutations/update-epic';
import { useBacklogDispatch } from '../../state';
import { cardDelete, type CardDeleteAction } from '../../state/actions/cards/delete';
import {
	cardRefreshRequest,
	type CardRefreshRequestAction,
} from '../../state/actions/cards/refresh';
import { closeDetailView, type CloseDetailViewAction } from '../../state/actions/detail-view';
import { issueViewUpdate, type IssueViewUpdateAction } from '../../state/actions/issue-view-update';
import {
	closeInsightsPanel,
	closeViewSettingsPanel,
	togglePanel,
	type CloseInsightsPanelAction,
	type CloseViewSettingsPanelAction,
	type TogglePanelAction,
} from '../../state/actions/panels';
import { cardStatusCheck, type CardStatusCheckAction } from '../../state/actions/status';
import { refetchVersions } from '../../state/actions/version';
import { getRapidViewId, getSessionId, getStoryPointFieldId } from '../../state/selectors/app';
import {
	getDetailViewData,
	getKeyByIdForAllIssueTypes,
	makeGetCardTypeHierarchyLevel,
} from '../../state/selectors/cards';
import { isBacklogWithSprints as isBacklogWithSprintsSelector } from '../../state/selectors/sprints';
import type { State } from '../../state/types';
import IssueWrapperDI from './issue-wrapper';
import DetailView, { type IssueDeleteArgs, type Props } from './view';

export type OwnProps = {
	boardId: number;
	parentWidth: number;
	onDelete: (issueId: string) => void;
};

export type ConnectedProps = {
	parentWidth: number;
	onDelete: (issueId: string) => void;
	onRefetchVersions: () => void;
	onUpdateEpicSummary: (rapidViewId: number, issueId: string, summary: string) => void;
	onCloseWithIssueId: (cardId: number) => CloseDetailViewAction;
};

export type StateProps = {
	isBacklogWithSprints: boolean;
	cardKey: CardKey | null;
	isEpic: boolean;
	timestamp: number;
	rapidViewId: string;
	cardId: number;
	externalAction: ExternalAction | undefined;
	backlogSessionId: string;
	storyPointFieldId: string | null;
};

export type DispatchProps = {
	onIssueViewUpdate: (payload: ChangeEvent) => IssueViewUpdateAction;
	onCardRefreshRequest: (cardId?: CardId, changeEvent?: ChangeEvent) => CardRefreshRequestAction;
	onTogglePanel: (panel: PanelType, isCompanyManaged: boolean) => TogglePanelAction;
	onCloseInsightsPanel: () => CloseInsightsPanelAction;
	onCloseViewSettingsPanel: () => CloseViewSettingsPanelAction;
	onDelete: (cardId: CardId) => CardDeleteAction;
	onCardStatusCheck: (payload: ChangeEvent) => CardStatusCheckAction;
};

const mapStateToProps = (state: State): StateProps => {
	const { cardId, timestamp, externalAction } = getDetailViewData(state) || {};
	const getIssueKey = getKeyByIdForAllIssueTypes(state);
	const cardKey = getIssueKey(cardId);
	const storyPointFieldId = getStoryPointFieldId(state);
	const isEpic = cardId
		? makeGetCardTypeHierarchyLevel(state)(cardId) === PARENT_HIERARCHY_TYPE
		: false;

	return {
		cardKey,
		// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
		cardId,
		isEpic,
		externalAction,
		// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
		timestamp,
		rapidViewId: getRapidViewId(state),
		isBacklogWithSprints: isBacklogWithSprintsSelector(state),
		backlogSessionId: getSessionId(state),
		storyPointFieldId,
	};
};

const mapDispatchToProps: DispatchProps = {
	onTogglePanel: togglePanel,
	onCloseInsightsPanel: () => closeInsightsPanel(),
	onCloseViewSettingsPanel: () => closeViewSettingsPanel(),
	onDelete: cardDelete,
	onIssueViewUpdate: issueViewUpdate,
	onCardRefreshRequest: cardRefreshRequest,
	onCardStatusCheck: cardStatusCheck,
};

const onCloseFactory = memoizeOne(
	(cardId: number, onCloseWithIssueId: (cardId: number) => CloseDetailViewAction) => () =>
		onCloseWithIssueId(cardId),
);
const onChangeFactory = memoizeOne(
	(
		storyPointFieldId: string | null,
		onRefetchVersions: () => void,
		isEpic: boolean,
		onUpdateEpicSummary: (rapidViewId: number, issueId: string, summary: string) => void,
		rapidViewId: string,
		onCardStatusCheck: (payload: ChangeEvent) => CardStatusCheckAction,
		onIssueViewUpdate: (payload: ChangeEvent) => IssueViewUpdateAction,
		onCardRefreshRequest: (cardId?: CardId, changeEvent?: ChangeEvent) => CardRefreshRequestAction,
	) =>
		(event: ChangeEvent) => {
			// NOTE: Updates for version panel are handled separately (owned by Fusion team: src/packages/software/version-backlog-panel/package.json)
			if (event.type === ChangeEventTypes.FIELD_CHANGED) {
				// Cannot remove StatusKey here as the fixed versions panel uses it
				// see https://stash.atlassian.com/projects/JIRACLOUD/repos/jira-frontend/pull-requests/32152/overview?commentId=2346562
				const versionsRefetchEventTypes: string[] = [
					StatusKey,
					FixVersionsKey,
					TimeEstimateKey,
					storyPointFieldId || '',
				];

				if (versionsRefetchEventTypes.includes(event.fieldId)) {
					onRefetchVersions();
				}

				if (event.meta.fieldId === 'summary') {
					const { issueId } = event;
					if (isEpic) {
						onUpdateEpicSummary(Number(rapidViewId), issueId, event.meta.fieldValue);
					}
				}

				// with Unmapped status feature, an issue can be moved back to backlog, if status is updated to mapped status
				// In that case backlog need to refresh the data so that issue can reappear on backlog.
				if (event.meta.fieldId === StatusKey) {
					onCardStatusCheck(event);
				}
			}

			// NOTE: Updates for extracted fields are managed by Sweet State and handled by field sync (src/packages/software/backlog/src/view/all-fields-sync/field-sync)
			const extractedFields: string[] = getExtractedFields({ storyPointFieldId });

			if (
				((event.type === ChangeEventTypes.FIELD_CHANGE_REQUESTED ||
					event.type === ChangeEventTypes.FIELD_CHANGED ||
					event.type === ChangeEventTypes.FIELD_CHANGE_FAILED) &&
					extractedFields.includes(event.meta.fieldId)) ||
				event.type === ChangeEventTypes.ISSUE_RELATIONSHIP_UPDATED
			) {
				return;
			}

			// Handles updates for epics or subtasks by updating only relevant parts of the Redux store (non-trivial for Backlog card UI but impact the issue hierarchy relationship)
			onIssueViewUpdate(event);

			const refreshIssueId =
				event.type === ChangeEventTypes.CHILD_ISSUE_ADDED
					? Number(event.meta.childIssue.id)
					: Number(event.issueId);

			// Handles updates for base-level issues by refetching the card (impact Backlog card UI directly)
			onCardRefreshRequest(refreshIssueId, event);
		},
);

const onDeleteFactory = memoizeOne(
	(
		ownPropsOnDelete: (issueId: string) => void,
		onDelete: (cardId: CardId) => CardDeleteAction,
		cardId: number,
		onCardRefreshRequest: (cardId?: CardId, changeEvent?: ChangeEvent) => CardRefreshRequestAction,
	) =>
		({ issueId }: IssueDeleteArgs) => {
			if (ownPropsOnDelete) {
				ownPropsOnDelete(issueId);
			}

			onDelete(Number(issueId));

			if (issueId && Number(issueId) !== cardId) {
				onCardRefreshRequest();
			}
		},
);

export const mergeProps = (
	{
		rapidViewId,
		backlogSessionId,
		storyPointFieldId,
		cardId,
		cardKey,
		isEpic,
		timestamp,
		isBacklogWithSprints,
		externalAction,
	}: StateProps,
	{
		onCloseInsightsPanel,
		onCloseViewSettingsPanel,
		onDelete,
		onIssueViewUpdate,
		onCardRefreshRequest,
		onCardStatusCheck,
	}: DispatchProps,
	{
		parentWidth,
		onDelete: ownPropsOnDelete,
		onRefetchVersions,
		onUpdateEpicSummary,
		onCloseWithIssueId,
	}: ConnectedProps,
): Props => ({
	parentWidth,
	cardKey,
	timestamp,
	rapidViewId,
	isBacklogWithSprints,
	externalAction,
	backlogSessionId,
	// extra method to avoid arguments in action creator
	onClose: onCloseFactory(cardId, onCloseWithIssueId),
	onCloseInsightsClick: onCloseInsightsPanel,
	onCloseViewSettingsPanelClick: onCloseViewSettingsPanel,
	onChange: onChangeFactory(
		storyPointFieldId,
		onRefetchVersions,
		isEpic,
		onUpdateEpicSummary,
		rapidViewId,
		onCardStatusCheck,
		onIssueViewUpdate,
		onCardRefreshRequest,
	),
	onDelete: onDeleteFactory(ownPropsOnDelete, onDelete, cardId, onCardRefreshRequest),
	IssueWrapper: IssueWrapperDI,
});

export const ConnectedDetailView = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps,
)(DetailView);

export const DetailViewWrapper = ({ boardId, ...props }: OwnProps) => {
	const { updateEpicSummary } = useUpdateEpicSummary();
	const dispatch = useBacklogDispatch();

	const refetch = useCallback(() => {
		dispatch(refetchVersions());
	}, [dispatch]);

	const onCloseWithIssueId = useCallback(
		(cardId: number): CloseDetailViewAction => {
			dispatch(closeDetailView(cardId));
			return closeDetailView(cardId);
		},
		[dispatch],
	);

	return (
		<ConnectedDetailView
			{...props}
			onRefetchVersions={refetch}
			onUpdateEpicSummary={updateEpicSummary}
			onCloseWithIssueId={onCloseWithIssueId}
		/>
	);
};

export default DetailViewWrapper;
